import React, { useState } from 'react'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableBody from '@mui/material/TableBody'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import UndoIcon from '@mui/icons-material/Undo'
import { useTranslation } from 'react-i18next'
import CardContentText from 'Components/reusable/CaleoCustomComponents/CardContentText'
import SkillPicker from 'Components/reusable/InputFields/SkillPicker'
import CertificatePicker from 'Components/reusable/InputFields/CertificatePicker'
import CompetencePicker from 'Components/reusable/InputFields/CompetencePicker'
import { IParsedCV, IParsedOptionChoices, ParsedOptionKind } from 'types/cvParserInterfaces'
import { ICertificate, ICompetence, ISkillOrIndustryOrRole } from 'types/cvsInterfaces'
import { chooseDBTranslation } from 'utils/translations'
import { parsedNewOptions, parsedOptionChoice, withParsedOptionChoice } from 'utils/parsedProfileFields'

/** @notExported */
interface INewOptionsPanelProps {
  /** Parsed CV */
  cv: IParsedCV
  /** Called with the reviewer's choices */
  onChange: (choices: IParsedOptionChoices) => void
  /** Display language */
  languageId: number
}

/** @notExported */
interface IExistingOptionPickerProps {
  kind: ParsedOptionKind
  label: string
  /** Called with the picked option's id and name */
  onPick: (optionId: number, name: string) => void
}

/**
 * Picker of existing options of a kind, without creating new ones
 *
 * @returns The picker
 * @notExported
 */
const ExistingOptionPicker: React.FC<IExistingOptionPickerProps> = ({ kind, label, onPick }) => {
  const { i18n } = useTranslation()

  if (kind === 'certificate') {
    return (
      <CertificatePicker
        value={null}
        allowCreate={false}
        insetLabel
        label={label}
        onChange={(picked: ICertificate | null) =>
          picked && onPick(picked.id, chooseDBTranslation(i18n, picked).certificate)
        }
      />
    )
  }
  if (kind === 'competence') {
    return (
      <CompetencePicker
        value={null}
        allowCreate={false}
        insetLabel
        label={label}
        onChange={(picked: ICompetence | null) => picked && onPick(picked.id, chooseDBTranslation(i18n, picked).name)}
      />
    )
  }
  return (
    <SkillPicker
      type={kind}
      value={null}
      allowCreate={false}
      showUnpromoted
      insetLabel
      label={label}
      onChange={(picked: ISkillOrIndustryOrRole | null) =>
        picked && onPick(picked.id, chooseDBTranslation(i18n, picked).name)
      }
    />
  )
}

/**
 * The CV's skill, role, industry, certificate and competence names that match no existing option, so saving would create them.
 * The reviewer can point each to an existing option instead; the choice applies wherever the name appears.
 *
 * @returns The panel, or nothing when every name matched
 * @notExported
 */
const NewOptionsPanel: React.FC<INewOptionsPanelProps> = ({ cv, onChange, languageId }) => {
  const { t } = useTranslation()
  // One picker at a time: each loads every option of its kind
  const [picking, setPicking] = useState<string | null>(null)

  const newOptions = parsedNewOptions(cv, languageId)
  if (newOptions.length === 0) {
    return null
  }
  const unchosen = newOptions.filter(option => !parsedOptionChoice(cv, option.kind, option.names)).length

  return (
    <>
      <CardContentText type="darkSubHeader">{t('admin.parse.result.newOptions', { count: unchosen })}</CardContentText>
      <CardContentText>{t('admin.parse.result.newOptionsInfo')}</CardContentText>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t('admin.parse.result.name')}</TableCell>
            <TableCell>{t('admin.parse.result.kind')}</TableCell>
            <TableCell>{t('admin.parse.result.existingOption')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {newOptions.map((option, index) => {
            const key = `${option.kind}-${option.label}`
            const choice = parsedOptionChoice(cv, option.kind, option.names)
            return (
              <TableRow key={key}>
                <TableCell>{option.label}</TableCell>
                <TableCell>{t(`admin.parse.result.kind.${option.kind}`)}</TableCell>
                <TableCell>
                  {choice ? (
                    <>
                      {choice.name}
                      <IconButton
                        data-testid={`button-admin-parse-new-option-undo-${index}`}
                        aria-label={t('admin.parse.result.undoExisting')}
                        size="small"
                        onClick={() =>
                          onChange(withParsedOptionChoice(cv.optionChoices, option.kind, option.names, null))
                        }
                      >
                        <UndoIcon fontSize="small" />
                      </IconButton>
                    </>
                  ) : picking === key ? (
                    <Box
                      sx={{ display: 'flex', alignItems: 'center' }}
                      data-testid={`picker-admin-parse-new-option-${index}`}
                    >
                      <ExistingOptionPicker
                        kind={option.kind}
                        label={t('admin.parse.result.useExisting')}
                        onPick={(optionId, name) => {
                          onChange(
                            withParsedOptionChoice(cv.optionChoices, option.kind, option.names, { optionId, name })
                          )
                          setPicking(null)
                        }}
                      />
                      <IconButton
                        aria-label={t('admin.parse.result.cancelExisting')}
                        size="small"
                        onClick={() => setPicking(null)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Button
                      data-testid={`button-admin-parse-new-option-pick-${index}`}
                      size="small"
                      onClick={() => setPicking(key)}
                    >
                      {t('admin.parse.result.useExisting')}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </>
  )
}

export default NewOptionsPanel
